/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/prop-types */
import { useState, useMemo, forwardRef, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  Stack,
  Checkbox,
} from '@mui/material';
import { TableVirtuoso, TableComponents } from 'react-virtuoso';
import { twMerge } from 'tailwind-merge';
import AppIcon from '@/components/shared/AppIcon/AppIcon';

import { ITableCell, ITableRow } from './Table';
import { useSelector } from 'react-redux';
import { BorderColor } from '@mui/icons-material';

export type ColumnSeletion = {
  label: string;
  value: boolean;
};

export type VirtualizedTableProps = {
  rows: ITableRow[];
  columns: ITableCell[];
  hoveredRowId?: string;
  onClickRow?: (apt: any) => void;
  onChangeHover?: (rowId: string | null) => void;
  onColumnSelected?: (value: ColumnSeletion) => void;
  height?: string;
  headerHeight?: string;
  rowHeight?: number;
  px?: number;
  classNames?: {
    container?: string;
  };
  showHeader?: boolean;
};

export function VirtualizedTable({
  rows,
  columns,
  headerHeight,
  height,
  rowHeight = 40,
  px = 32,
  onClickRow,
  hoveredRowId,
  onChangeHover,
  onColumnSelected,
  classNames,
  showHeader = true,
}: VirtualizedTableProps) {
  const [sortKey, setSortKey] = useState<string>('');
  const [ascOrder, setAscOrder] = useState<boolean>(true);
  const [selectedColumn, setSelectedColumn] = useState<string | undefined>(
    undefined
  );

  const [localHoveredRowId, setLocalHoveredRowId] = useState<string | null>(
    null
  );

  const visualizer = useSelector((state: any) => state.project.projectVisualizer);

  const tblRows = useMemo(() => {
    // Default sorting key is 'Unit' if no sortKey is provided
    const activeSortKey = sortKey || 'Unit';

    return rows.sort((a, b) => {
      let first = a.data[activeSortKey]?.value;
      let second = b.data[activeSortKey]?.value;

      // Normalize `undefined` and `"undefined"` to `null`
      if (first === undefined || first === 'undefined') first = null;
      if (second === undefined || second === 'undefined') second = null;

      const direction = ascOrder || ascOrder === null ? 1 : -1;

      // Handle null values (previously undefined/"undefined")
      if (first === null && second === null) return 1 * direction;
      if (first === null) return -1 * direction;
      if (second === null) return 1 * direction;

      if (activeSortKey === 'Unit') {
        const regex = /^(\d+)([A-Za-z]*)$/;

        const matchA = typeof first === 'string' ? first.match(regex) : null;
        const matchB = typeof second === 'string' ? second.match(regex) : null;

        // Ensure matches exist before destructuring
        if (!matchA || !matchB) {
          return String(first).localeCompare(String(second)) * direction;
        }

        const [, numA, suffixA] = matchA;
        const [, numB, suffixB] = matchB;

        const numberComparison = ascOrder
          ? Number(numA) - Number(numB)
          : Number(numB) - Number(numA);

        if (numberComparison !== 0) return numberComparison;
        return suffixA.localeCompare(suffixB) * direction;
      } else {
        if (first === second) return 0;
        return first > second ? 1 * direction : -1 * direction;
      }
    });
  }, [sortKey, rows, ascOrder]);

  const handleDir = useCallback(
    (label: string) => {
      if (ascOrder === null) {
        setAscOrder(true);
        return;
      }

      if (sortKey === label) {
        setAscOrder((prevOrder) => !prevOrder);
      } else {
        setSortKey(label);
      }
    },
    [ascOrder, sortKey]
  );

  const components = useMemo(() => {
    return {
      Scroller: forwardRef<HTMLDivElement>((props, ref) => (
        <TableContainer
          component="div"
          ref={ref}
          className={twMerge(
            'rounded-xl border border-zinc-600' ,
            classNames?.container
          )}
          sx={{ height }}
          {...props}
        />
      )),
      Table: (props) => <Table {...props} className="relative h-auto w-full" />,
      TableHead,
      TableRow: ({ item, ...props }) => (
        <TableRow
          className="w-full"
          sx={{
            cursor: onClickRow ? 'pointer' : '',
            height: rowHeight,
          }}
          {...props}
        />
      ),
      TableBody: forwardRef<HTMLTableSectionElement>((props, ref) => (
        <TableBody {...props} ref={ref} />
      )),
      EmptyPlaceholder: () => (
        <Typography variant="caption" className="pl-10 text-stone-200">
          No data to display
        </Typography>
      ),
    } as TableComponents<ITableRow>;
  }, [classNames?.container, height, onClickRow, rowHeight]);

  const headerContent = () => (
    <TableRow>
      {columns.map(
        (
          {
            showCheckbox,
            sortable,
            label,
            children,
            value,
            className,
            ...rest1
          },
          index
        ) => (
          <TableCell
            key={label}
            {...rest1}
            className="group cursor-pointer p-0"
            onClick={() => handleDir(label)}
          >
            <Stack
              direction="row"
              alignItems="center"
              className={twMerge('h-40 border-b border-zinc-600', className)}
              sx={{
                paddingLeft: index === 0 ? `${px}px` || '32px' : '0',
                paddingRight:
                  index === columns.length - 1 ? `${px}px` || '32px' : 0,
                fontSize: headerHeight ? '12px' : '16px',
              }}
            >
              {showCheckbox ? (
                <Checkbox
                  className="-ml-2 py-0 pl-0 pr-2"
                  checked={selectedColumn === label}
                  onChange={(event) => {
                    if (selectedColumn === label) {
                      setSelectedColumn(undefined);
                      if (onColumnSelected) {
                        onColumnSelected({ label: '', value: false });
                      }
                      return;
                    }

                    setSelectedColumn(label);
                    if (onColumnSelected)
                      onColumnSelected({ label, value: event.target.checked });
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : null}
              {children || label}
              {sortable ? (
                <AppIcon
                  name={
                    label === sortKey && ascOrder !== null
                      ? ascOrder
                        ? 'sortAsc'
                        : 'sortDesc'
                      : 'sort'
                  }
                />
              ) : null}
            </Stack>
          </TableCell>
        )
      )}
    </TableRow>
  );

  return (
    <TableVirtuoso
      data={tblRows}
      components={components}
      fixedHeaderContent={showHeader ? headerContent : undefined}
      itemContent={(_index: number, row: ITableRow) => (
        <>
          {columns.map((column, index) => (
            <TableCell
              key={column.label}
              {...row.data[column.label]}
              className="p-0"
              onClick={() => {
                console.log('Row Clicked!', row);
                // dispatch(
                //   setSelectedUnit({ apartmentId: row.id.toUpperCase() })
                // );
                onClickRow && onClickRow(row);
              }}
              onMouseEnter={() => {
                setLocalHoveredRowId(row.id);
                onChangeHover && onChangeHover(row.id)}}
              onMouseLeave={() => {
                setLocalHoveredRowId(null);
                onChangeHover && onChangeHover(null)}}
              sx={{
                '&.MuiTableCell-hover': {
                  background: onClickRow ? 'rgba(255,255,255,0.12)' : '',
                },
                background:
                  row.id === localHoveredRowId || hoveredRowId ? 'rgba(255, 255, 255, 0.2)' : '',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                className={twMerge(`overflow-hidden text-sm text-zinc-400 ${row.data[column.label] && row.data[column.label].className}`,
                )}
                sx={{
                  paddingLeft: index === 0 ? `${px}px` || '32px' : '0',
                  paddingRight:
                    index === columns.length - 1 ? `${px}px` || '32px' : 0,
                  fontSize: headerHeight ? '12px' : '16px',
                  height: rowHeight,                  
                }}
                gap="8px"
              >
                {selectedColumn && selectedColumn === column.label ? (
                  <div className='w-16 h-16 rounded-sm' style={{
                    backgroundColor: selectedColumn && selectedColumn === column.label && visualizer ? visualizer.getColor(row.data[column.label].value) : ''
                  }}>

                  </div>
                ) : null}
                <div>
                  {row.data[column.label] && row.data[column.label].children
                    ? row.data[column.label].children
                    : row.data[column.label].label}
                </div>
              </Stack>
            </TableCell>
          ))}
        </>
      )}
      defaultItemHeight={rowHeight}
    />
  );
}
